import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Wrench, CheckCircle, Clock, Shield, Award, Phone } from 'lucide-react';

const MechanicalServiceSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeService, setActiveService] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Auto-rotate services
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveService(prev => (prev + 1) % services.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const services = [
    {
      icon: '🔍',
      title: 'Révision moteur',
      description: 'Révision complète et maintenance préventive',
      features: ['Check-up 60 points', 'Rapport détaillé', 'Conseils experts'],
      color: 'from-purple-500 to-violet-600'
    },
    {
      icon: '💻',
      title: 'Diagnostic',
      description: 'Diagnostic électronique et réparation informatique',
      features: ['Outils professionnels', 'Tous véhicules', 'Rapport précis'],
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: '🔧',
      title: 'Freinage',
      description: 'Changement de plaquettes, disques et système complet',
      features: ['Diagnostic gratuit', 'Pièces certifiées', 'Garantie 12 mois'],
      color: 'from-red-500 to-pink-600'
    },
    {
      icon: '🔋',
      title: 'Batterie',
      description: 'Test, maintenance et remplacement de batteries',
      features: ['Test électronique', 'Installation comprise', 'Recyclage ancien'],
      color: 'from-blue-500 to-indigo-600'
    },
    {
      icon: '⚙️',
      title: 'Amortisseurs',
      description: 'Diagnostic et remplacement des amortisseurs',
      features: ['Contrôle gratuit', 'Géométrie incluse', 'Marques premium'],
      color: 'from-green-500 to-emerald-600'
    }
  ];

  const advantages = [
    {
      icon: Clock,
      title: 'Service rapide',
      description: 'Intervention dans les délais convenus'
    },
    {
      icon: Shield,
      title: 'Pièces de qualité',
      description: 'Uniquement des pièces certifiées et garanties'
    },
    {
      icon: Award,
      title: 'Techniciens experts',
      description: 'Équipe formée et expérimentée'
    }
  ];

  return (
    <section 
      ref={sectionRef}
      className="py-20 bg-white relative overflow-hidden"
      itemScope
      itemType="https://schema.org/Service"
    >
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 right-10 w-32 h-32 bg-gray-100 rounded-full opacity-50 animate-pulse" />
        <div className="absolute bottom-20 left-10 w-24 h-24 bg-blue-100 rounded-full opacity-50 animate-pulse" style={{ animationDelay: '1s' }} />
        
        {/* Floating Tools */}
        <div className="absolute inset-0 opacity-5">
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-float text-2xl"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${4 + Math.random() * 2}s`
              }}
            >
              {['🔧', '🔩', '⚙️', '🛠️'][Math.floor(Math.random() * 4)]}
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-800 rounded-full text-sm font-medium mb-4">
            <Wrench className="w-4 h-4 mr-2" />
            Atelier physique - Bouskoura
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-gray-700 to-gray-900">
              Mécanique
            </span>{' '}
            professionnelle
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" itemProp="description">
            Confiez-nous vos travaux de mécanique dans notre atelier moderne. 
            Service rapide, transparent, avec des pièces de qualité et des délais maîtrisés.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          
          {/* Services Grid */}
          <div className={`space-y-8 transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}>
            
            {/* Service Categories */}
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Nos spécialités mécaniques</h3>
              
              <div className="grid gap-4">
                {services.map((service, index) => {
                  const isActive = index === activeService;
                  
                  return (
                    <div
                      key={index}
                      className={`
                        group relative p-6 rounded-xl border-2 transition-all duration-500 cursor-pointer
                        ${isActive 
                          ? 'bg-gradient-to-r ' + service.color + ' text-white border-transparent shadow-xl scale-105' 
                          : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-lg'
                        }
                      `}
                      onMouseEnter={() => setActiveService(index)}
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`
                          text-4xl transition-transform duration-300
                          ${isActive ? 'scale-110' : 'group-hover:scale-110'}
                        `}>
                          {service.icon}
                        </div>
                        
                        <div className="flex-1">
                          <h4 className={`text-xl font-bold mb-2 ${
                            isActive ? 'text-white' : 'text-gray-900'
                          }`}>
                            {service.title}
                          </h4>
                          
                          <p className={`mb-4 ${
                            isActive ? 'text-white/90' : 'text-gray-600'
                          }`}>
                            {service.description}
                          </p>
                          
                          {/* Features */}
                          <div className={`
                            space-y-2 transition-all duration-500
                            ${isActive ? 'opacity-100 max-h-40' : 'opacity-0 max-h-0 overflow-hidden group-hover:opacity-100 group-hover:max-h-40'}
                          `}>
                            {service.features.map((feature, idx) => (
                              <div 
                                key={idx}
                                className="flex items-center text-sm"
                                style={{ transitionDelay: `${idx * 100}ms` }}
                              >
                                <CheckCircle className={`w-4 h-4 mr-2 flex-shrink-0 ${
                                  isActive ? 'text-white' : 'text-green-500'
                                }`} />
                                <span className={isActive ? 'text-white' : 'text-gray-700'}>
                                  {feature}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      {/* Active indicator */}
                      {isActive && (
                        <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full animate-pulse" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Advantages */}
            <div className="space-y-4">
              <h4 className="text-xl font-bold text-gray-900">Pourquoi notre atelier ?</h4>
              <div className="space-y-3">
                {advantages.map((advantage, index) => {
                  const Icon = advantage.icon;
                  return (
                    <div 
                      key={index}
                      className={`
                        flex items-center space-x-4 p-4 bg-gray-50 rounded-xl
                        transition-all duration-500 hover:bg-gray-100 hover:scale-105
                        ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}
                      `}
                      style={{ transitionDelay: `${(index + 6) * 200}ms` }}
                    >
                      <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Icon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900">{advantage.title}</h5>
                        <p className="text-sm text-gray-600">{advantage.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* CTA */}

          </div>

          {/* Workshop Visual */}
          <div className={`relative transition-all duration-1000 delay-500 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}>
            
            {/* Main Workshop Image */}
            <div className="relative">
              <div className="relative bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/images/garage.png"
                  alt="Atelier de mécanique automobile Faucon Bleu"
                  className="w-full h-96 object-cover"
                  loading="lazy"
                  itemProp="image"
                />
                
                {/* Overlay with stats */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-2xl font-bold mb-4">Atelier moderne à Bouskoura</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold">1000m²</div>
                      <div className="text-sm opacity-90">Surface</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">8</div>
                      <div className="text-sm opacity-90">Postes de travail</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">15+</div>
                      <div className="text-sm opacity-90">Années d'expérience</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Stats */}
              <div className="absolute -top-4 -left-4 bg-white rounded-xl p-4 shadow-lg border animate-float">
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600">24h</div>
                  <div className="text-xs text-gray-600">Délai moyen</div>
                </div>
              </div>

              <div className="absolute -bottom-4 -right-4 bg-white rounded-xl p-4 shadow-lg border animate-float-delayed">
                <div className="text-center">
                  <div className="text-lg font-bold text-green-600">98%</div>
                  <div className="text-xs text-gray-600">Satisfaction</div>
                </div>
              </div>

              <div className="absolute top-1/4 -right-6 bg-white rounded-xl p-3 shadow-lg border animate-float">
                <div className="text-center">
                  <div className="text-lg font-bold text-purple-600">Pro</div>
                  <div className="text-xs text-gray-600">Équipement</div>
                </div>
              </div>
            </div>

            {/* Equipment highlights */}
            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="text-center">
                  <div className="text-3xl mb-2">🔧</div>
                  <h4 className="font-semibold text-gray-900">Outils professionnels</h4>
                  <p className="text-sm text-gray-600">Équipement de dernière génération</p>
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="text-center">
                  <div className="text-3xl mb-2">🏆</div>
                  <h4 className="font-semibold text-gray-900">Certifications</h4>
                  <p className="text-sm text-gray-600">Agréé assurences</p>
                </div>
              </div>
            </div>
                            <img
                  src="/images/garage2.jpg"
                  alt="Atelier de mécanique automobile Faucon Bleu"
                  className="w-full h-96 object-cover mt-8"
                  loading="lazy"
                  itemProp="image"
                />
                 
          </div>
        </div>
        
      </div>
            <div className={`transition-all duration-1000 flex justify-center mt-8 delay-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}>
              <a
                href="tel:+212608112626"
                className="group inline-flex items-center justify-center w-auto sm:w-auto px-8 py-4 bg-gradient-to-r 
                scale-125
                from-gray-700 to-gray-900 text-white font-bold rounded-xl transition-all duration-300 transform hover:scale-150 hover:shadow-xl"
              >
                <Phone className="w-5 h-5 mr-3" />
                <span>Prendre rendez-vous</span>
                <ArrowRight className="ml-3 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
      {/* Structured Data */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Mécanique automobile - Atelier physique",
            "description": "Service de mécanique automobile en atelier: freins, batteries, amortisseurs, révisions périodiques, diagnostics électroniques. Service rapide avec pièces de qualité.",
            "provider": {
              "@type": "AutoRepair",
              "name": "Faucon Bleu",
              "url": "https://faucon-bleu.ma",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Bouskoura",
                "addressRegion": "Casablanca",
                "addressCountry": "MA"
              }
            },
            "areaServed": {
              "@type": "City",
              "name": "Casablanca",
              "addressCountry": "MA"
            },
            "serviceType": "Automotive Repair",
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Services de mécanique",
              "itemListElement": services.map((service, index) => ({
                "@type": "Offer",
                "position": index + 1,
                "itemOffered": {
                  "@type": "Service",
                  "name": service.title,
                  "description": service.description
                }
              }))
            }
          })
        }}
      />

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-8px) rotate(2deg); }
          66% { transform: translateY(-4px) rotate(-2deg); }
        }
        
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-6px) rotate(1deg); }
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-float-delayed {
          animation: float-delayed 3s ease-in-out infinite 1.5s;
        }
      `}</style>
    </section>
  );
};

export default MechanicalServiceSection;